import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Crown, Sparkles, Check } from 'lucide-react';
import { useAuth, API } from '../App';
import { useTranslation } from 'react-i18next';
import PaymentDialog from './PaymentDialog';

export default function UpgradeToCreatorButton() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  // Only show if user has Premium but not Creator
  if (!user?.premium_status || user?.creator_status) {
    return null;
  }

  const creatorFeatures = [
    { icon: '🎓', text: t('upgrade.createCourses') },
    { icon: '👥', text: t('upgrade.groupSessions') },
    { icon: '📊', text: t('upgrade.analytics') },
    { icon: '⭐', text: t('upgrade.priority') },
    { icon: '📧', text: t('upgrade.emailPromo') },
    { icon: '💰', text: t('upgrade.monetize') }
  ];

  return (
    <>
      <Button
        onClick={() => setShowDialog(true)}
        size="sm"
        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white gap-2 shadow-lg"
      >
        <Crown className="h-4 w-4" />
        {t('upgrade.upgradeCreator')}
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Crown className="h-6 w-6 text-purple-500" />
              {t('upgrade.becomeCreator')}
            </DialogTitle>
            <DialogDescription>
              {t('upgrade.unlockPotential')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Price */}
            <div className="text-center bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-6 rounded-lg">
              <p className="text-4xl font-bold text-purple-600 dark:text-purple-400">£9.88</p>
              <p className="text-gray-600 dark:text-gray-300">{t('upgrade.perMonth')}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {t('upgrade.cancelAnytime')}
              </p>
            </div>

            {/* Features */}
            <div>
              <h4 className="font-bold text-gray-900 dark:text-white mb-4">
                {t('upgrade.creatorBenefits')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {creatorFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-purple-100 dark:border-purple-800">
                    <span className="text-2xl">{feature.icon}</span>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{feature.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* All Premium features included */}
            <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Check className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                <p className="font-bold text-teal-900 dark:text-teal-100">
                  {t('upgrade.plusAllPremium')}
                </p>
              </div>
              <p className="text-sm text-teal-700 dark:text-teal-300">
                {t('upgrade.keepPremium')}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                setShowDialog(false);
                setShowPayment(true);
              }}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
            >
              {t('upgrade.upgradeNow')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog for Creator Upgrade */}
      <PaymentDialog
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        plan="creator"
      />
    </>
  );
}
